/**
 * DashboardCardShell — glass widget-card chrome for the OEM Home Dashboard.
 *
 * Ports the reference `internal/os/apps/web/src/pages/home/DashboardCard.tsx`
 * to React Native Web + Crop Circle tokens. The reference uses Tailwind class
 * utilities + framer-motion; here we use RNW StyleSheet + Crop Circle tokens.
 * No framer-motion dependency (web-only dashboard, no animation required in P1).
 *
 * The glass surface (semi-transparent white + backdrop blur) floats over the map.
 *
 * Slots:
 *  - title / subtitle / action — header row
 *  - children — body content
 *  - loadingSlot / emptySlot / errorSlot — state overrides (render instead of children)
 */
import React from 'react';
import { Platform, Pressable, StyleSheet, Text, View, ViewStyle } from 'react-native';
import { Colors, tokens } from '@gripp/shared-ui';

export interface DashboardCardShellProps {
  /** Card title (rendered in header). Required. */
  title: string;
  /** Optional subtitle shown beside the title in a lighter weight. */
  subtitle?: string;
  /** Optional right-side header action element (e.g. "View all" link). */
  action?: React.ReactNode;
  /** Body content — rendered unless a state slot overrides. */
  children: React.ReactNode;
  /** When provided, renders instead of `children`. */
  loadingSlot?: React.ReactNode;
  /** When provided, renders instead of `children`. */
  emptySlot?: React.ReactNode;
  /** When provided, renders instead of `children`. */
  errorSlot?: React.ReactNode;
  /** Outer container style override. */
  style?: ViewStyle;
  /**
   * Let children render outside the card bounds (overflow: visible) instead of
   * being clipped. Needed when a child paints beyond the card — e.g. the
   * Activity chart's hover tooltip floats above the panel over the map. The
   * rounded glass corners are preserved (border-radius + backdrop-filter still
   * clip the surface; only child clipping is dropped).
   */
  allowOverflow?: boolean;
  /**
   * When provided, the header's title/subtitle region becomes a Pressable that
   * fires this on press — used by the map-plottable cards (Activity / Assets /
   * Routines) to toggle the map's plotting mode. Kept SEPARATE from `action`
   * (View all) and the body's own pressables: they're sibling regions, never
   * nested, so a click lands on exactly one (RNW Pressables don't stop
   * propagation, so nesting would double-fire).
   */
  onHeaderPress?: () => void;
  /** Highlights the card (selected ring) — paired with `onHeaderPress`. */
  selected?: boolean;
  /** Accessible label for the header-press control (e.g. "Plot Assets on map"). */
  headerPressLabel?: string;
  /** Test ID for the card container. */
  testID?: string;
}

export const DashboardCardShell: React.FC<DashboardCardShellProps> = ({
  title,
  subtitle,
  action,
  children,
  loadingSlot,
  emptySlot,
  errorSlot,
  style,
  allowOverflow = false,
  onHeaderPress,
  selected = false,
  headerPressLabel,
  testID,
}) => {
  const body = errorSlot ?? emptySlot ?? loadingSlot ?? children;

  const headerLeftContent = (
    <>
      <Text style={styles.title} numberOfLines={1}>{title}</Text>
      {subtitle ? (
        <Text style={styles.subtitle} numberOfLines={1}>{subtitle}</Text>
      ) : null}
    </>
  );

  return (
    <View
      style={[
        styles.card,
        allowOverflow && styles.cardOverflowVisible,
        selected && styles.cardSelected,
        style,
      ]}
      testID={testID}
    >
      {/* Header row */}
      <View style={styles.header}>
        {onHeaderPress ? (
          <Pressable
            style={styles.headerLeft}
            onPress={onHeaderPress}
            accessibilityRole="button"
            accessibilityLabel={headerPressLabel ?? title}
            accessibilityState={{ selected }}
            testID={testID ? `${testID}-header-press` : undefined}
          >
            {headerLeftContent}
          </Pressable>
        ) : (
          <View style={styles.headerLeft}>{headerLeftContent}</View>
        )}
        {action && <View style={styles.headerRight}>{action}</View>}
      </View>

      {/* Body */}
      <View style={styles.body}>{body}</View>
    </View>
  );
};

DashboardCardShell.displayName = 'DashboardCardShell';

/**
 * ViewAllLink — small pressable "View all" text link used in card actions.
 * Wraps a Pressable so it has an accessible role and hit target.
 */
export interface ViewAllLinkProps {
  label?: string;
  onPress: () => void;
  testID?: string;
}

export const ViewAllLink: React.FC<ViewAllLinkProps> = ({
  label = 'View all',
  onPress,
  testID,
}) => {
  const [hovered, setHovered] = React.useState(false);
  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="link"
      accessibilityLabel={label}
      testID={testID}
      style={styles.viewAllPressable}
    >
      <Text style={[styles.viewAllText, hovered && styles.viewAllTextHovered]}>
        {label}
      </Text>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  card: {
    // Fill the sized strip wrapper (design surface is h-full in its StripCard).
    flex: 1,
    borderRadius: tokens.borderRadius.lg, // 8px (design: rounded-[8px])
    borderWidth: 1,
    borderColor: Colors.role.borderSection,
    overflow: 'hidden',
    // Glass surface — the card fill at 85% (design: bg-card/85). On web, add
    // backdrop-filter via Platform.select so RNW doesn't try to parse it on
    // native.
    backgroundColor: Colors.role.surfaceGlass,
    ...Platform.select({
      web: {
        backdropFilter: 'blur(12px) saturate(1.5)',
        WebkitBackdropFilter: 'blur(12px) saturate(1.5)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
      } as ViewStyle,
    }),
  },
  // Opt-in (allowOverflow): drop child clipping so e.g. the chart tooltip can
  // float above the card. Corners stay rounded via border-radius + the glass
  // backdrop-filter, which clip the surface independently of overflow.
  cardOverflowVisible: {
    overflow: 'visible',
  },
  // Selected (plotting on the map): brand-orange ring. 2px border replaces the
  // 1px slate border, so the card doesn't shift size; the extra 1px is absorbed
  // by the card's own padding.
  cardSelected: {
    borderWidth: 2,
    borderColor: Colors.role.brandOnSurface,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: tokens.spacing.lg, // px-4 (16)
    paddingTop: tokens.spacing.lg, // pt-4 (16)
    paddingBottom: tokens.spacing.sm, // pb-2 (8)
    gap: tokens.spacing.sm,
  },
  headerLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: tokens.spacing.sm,
    flexShrink: 1,
  },
  headerRight: {
    flexShrink: 0,
  },
  title: {
    ...tokens.typography.label.medium,
    // design: text-[13px] leading-[18px] — an arbitrary size between
    // label.medium (12/16) and label.large (14/20), so override on top of the
    // nearest token rather than introduce a one-off token.
    fontSize: 13,
    lineHeight: 18,
    fontWeight: '600',
    color: Colors.role.textStrong,
  },
  subtitle: {
    ...tokens.typography.label.medium,
    color: Colors.role.textSoft,
  },
  body: {
    flex: 1,
    paddingHorizontal: tokens.spacing.lg, // px-4 (16)
    paddingBottom: tokens.spacing.lg, // pb-4 (16)
  },
  viewAllPressable: {
    paddingVertical: tokens.spacing.xxs,
    paddingHorizontal: tokens.spacing.xs,
  },
  // Secondary action: soft grey, darkening on hover (design: text-foreground-soft
  // hover:text-foreground-strong) — not the brand-orange primary action color.
  viewAllText: {
    ...tokens.typography.label.medium,
    color: Colors.role.textSoft,
  },
  viewAllTextHovered: {
    color: Colors.role.textStrong,
  },
});
